import type { MailPlaceholderValueType } from "api/mailTemplates/MailTemplate.typed";
import {
  MAIL_PLACEHOLDER_TYPE_STRING,
  MAIL_PLACEHOLDER_TYPE_URL,
} from "api/mailTemplates/MailTemplate.typed";
import { escapeHtmlAttr } from "./mailLineParts";
import { MAIL_GOOGLE_STARS_HTML, mailGoogleGIconHtml } from "./mailEmailSafeIcons";

export type MailGoogleReviewBlock = {
  id: string;
  type: "googleReview";
  href: string;
};

export const MAIL_GOOGLE_REVIEW_FIELD_KEY = "href" as const;

export const MAIL_GOOGLE_REVIEW_CTA_LABEL = "Avis Google";

const GO_PH_TOKEN_RE = /^\{\{\.[A-Za-z0-9_]+\}\}$/;

function isGoPlaceholderToken(raw: string): boolean {
  return GO_PH_TOKEN_RE.test(raw.trim());
}

function hasUrlScheme(raw: string): boolean {
  const lower = raw.trim().toLowerCase();

  return lower.startsWith("http://") || lower.startsWith("https://");
}

export function mailPlaceholderValueTypesForGoogleReview(): ReadonlySet<MailPlaceholderValueType> {
  return new Set([MAIL_PLACEHOLDER_TYPE_URL, MAIL_PLACEHOLDER_TYPE_STRING]);
}

export function createEmptyMailGoogleReviewBlock(blockId: string): MailGoogleReviewBlock {
  return { id: blockId, type: "googleReview", href: "" };
}

export function mailGoogleReviewHref(value: string): string {
  const raw = value.trim();

  if (!raw) {
    return "";
  }

  if (hasUrlScheme(raw) || isGoPlaceholderToken(raw)) {
    return raw;
  }

  return `https://${raw}`;
}

function mailGoogleReviewAnchor(href: string, inner: string): string {
  return `<a href="${href}" style="text-decoration:none;color:#111827;display:inline-block;" target="_blank" rel="noopener noreferrer">${inner}</a>`;
}

export function serializeMailGoogleReviewBlock(block: MailGoogleReviewBlock): string {
  const href = mailGoogleReviewHref(block.href);
  const hrefEsc = escapeHtmlAttr(block.href);
  const hrefAttr = escapeHtmlAttr(href);
  const label = escapeHtmlAttr(MAIL_GOOGLE_REVIEW_CTA_LABEL);
  const gIcon = mailGoogleGIconHtml();
  const gCell = href ? mailGoogleReviewAnchor(hrefAttr, gIcon) : gIcon;
  const starsCell = href
    ? mailGoogleReviewAnchor(hrefAttr, MAIL_GOOGLE_STARS_HTML)
    : MAIL_GOOGLE_STARS_HTML;
  const labelCell = href ? mailGoogleReviewAnchor(hrefAttr, label) : label;
  const inner = `<table role="presentation" cellpadding="0" cellspacing="0" align="center" style="border-collapse:collapse;"><tr><td align="center" style="padding:0 0 6px 0;">${gCell}</td></tr><tr><td align="center" style="padding:0;line-height:20px;font-size:18px;">${starsCell}</td></tr><tr><td align="center" style="padding:8px 0 0 0;font-family:Verdana,Geneva,sans-serif;font-size:13px;font-weight:600;color:#111827;">${labelCell}</td></tr></table>`;

  return `<table role="presentation" data-mail-google-review="1" data-mail-google-review-href="${hrefEsc}" cellpadding="0" cellspacing="0" align="center" style="border-collapse:collapse;margin:16px auto;"><tr><td align="center" style="padding:0;">${inner}</td></tr></table>`;
}

export function parseMailGoogleReviewTable(
  el: HTMLElement,
  blockId: string,
): MailGoogleReviewBlock | null {
  if (el.getAttribute("data-mail-google-review") !== "1") {
    return null;
  }

  const fromAttr = (el.getAttribute("data-mail-google-review-href") || "").trim();
  const fromAnchor = (el.querySelector("a")?.getAttribute("href") || "").trim();

  return {
    id: blockId,
    type: "googleReview",
    href: fromAttr || fromAnchor,
  };
}
